/**
 * Form Settings Card Component
 *
 * Unified settings card containing all form configuration:
 * - General settings (active status, keyboard nav)
 * - Rating behavior (threshold, step 2, Google redirect, email/rewards)
 * - Display settings (animations, progress bar, step indicator)
 * - Text customization
 */

import { Settings, Star, Mail, Gift, ExternalLink, ThumbsUp, ThumbsDown, Ticket, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormConfig } from './hooks/useFormBuilder';
import { ReviewSettings, RewardType } from '@/lib/forms/templates';
import { useCouponOffers, useLoyaltyPrograms } from '@/hooks/forms';

interface FormSettingsCardProps {
  form: FormConfig;
  businessId: string;
  onUpdate: (updates: Partial<FormConfig>) => void;
  onUpdateUIText: (key: keyof NonNullable<FormConfig['uiTextConfig']>, value: string) => void;
  reviewSettings: ReviewSettings;
  onUpdateReviewSettings: (settings: Partial<ReviewSettings>) => void;
  showReviewSettings?: boolean;
  className?: string;
}

export function FormSettingsCard({
  form,
  businessId,
  onUpdate,
  onUpdateUIText,
  reviewSettings,
  onUpdateReviewSettings,
  showReviewSettings = true,
  className,
}: FormSettingsCardProps) {
  const { data: couponOffers = [], isLoading: couponsLoading } = useCouponOffers(businessId);
  const { data: loyaltyPrograms = [], isLoading: loyaltyLoading } = useLoyaltyPrograms(businessId);

  // Generate Google Review URL from place ID
  const googleReviewUrl = reviewSettings.googleReviewUrlOverride ||
    (reviewSettings.googlePlaceId
      ? `https://search.google.com/local/writereview?placeid=${reviewSettings.googlePlaceId}`
      : '');

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Settings className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <CardTitle className="text-base">Form Settings</CardTitle>
            <CardDescription className="text-xs">
              Configure behavior, appearance, and rating flow
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs defaultValue="general" className="w-full">
          <TabsList className={cn("w-full grid", showReviewSettings ? "grid-cols-4" : "grid-cols-3")}>
            <TabsTrigger value="general" className="text-xs">General</TabsTrigger>
            {showReviewSettings && (
              <TabsTrigger value="rating" className="text-xs">Rating Behavior</TabsTrigger>
            )}
            <TabsTrigger value="display" className="text-xs">Display</TabsTrigger>
            <TabsTrigger value="text" className="text-xs">Text</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="active-status">Form is active</Label>
                <p className="text-xs text-muted-foreground">
                  Allow submissions to this form
                </p>
              </div>
              <Switch
                id="active-status"
                checked={form.activeStatus}
                onCheckedChange={(checked) => onUpdate({ activeStatus: checked })}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="keyboard-nav">Keyboard navigation</Label>
                <p className="text-xs text-muted-foreground">
                  Use arrow keys and Enter to navigate
                </p>
              </div>
              <Switch
                id="keyboard-nav"
                checked={form.keyboardNavEnabled}
                onCheckedChange={(checked) =>
                  onUpdate({ keyboardNavEnabled: checked })
                }
              />
            </div>
          </TabsContent>

          {/* Rating Behavior Settings */}
          {showReviewSettings && (
            <TabsContent value="rating" className="space-y-4 pt-4">
              {/* Rating Threshold */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Rating Threshold</Label>
                    <p className="text-xs text-muted-foreground">
                      {reviewSettings.starThreshold}+ stars redirect to Google Reviews
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => onUpdateReviewSettings({ starThreshold: star })}
                        className={cn(
                          'p-1 rounded transition-colors',
                          star <= reviewSettings.starThreshold
                            ? 'text-yellow-400'
                            : 'text-muted-foreground hover:text-muted-foreground/80'
                        )}
                      >
                        <Star className={cn(
                          'h-5 w-5',
                          star <= reviewSettings.starThreshold && 'fill-current'
                        )} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Google Review URL */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="google-url" className="text-sm font-medium">
                    Google Reviews URL
                  </Label>
                  {googleReviewUrl && (
                    <a
                      href={googleReviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      Test <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <Input
                  id="google-url"
                  value={reviewSettings.googleReviewUrlOverride || googleReviewUrl}
                  onChange={(e) => onUpdateReviewSettings({ googleReviewUrlOverride: e.target.value || undefined })}
                  placeholder="https://search.google.com/local/writereview?placeid=..."
                  className="text-sm h-8"
                />
              </div>

              <Separator />

              {/* Follow-up Questions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable-step2">Follow-up questions</Label>
                    <p className="text-xs text-muted-foreground">
                      Show questions after the rating
                    </p>
                  </div>
                  <Switch
                    id="enable-step2"
                    checked={form.enableStep2}
                    onCheckedChange={(checked) => onUpdate({ enableStep2: checked })}
                  />
                </div>

                {form.enableStep2 && (
                  <div className="pl-4 space-y-2 border-l-2 border-muted">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="enable-step2-high" className="text-sm flex items-center gap-1.5">
                        <ThumbsUp className="h-3.5 w-3.5 text-green-500" />
                        For high ratings ({reviewSettings.starThreshold}+)
                      </Label>
                      <Switch
                        id="enable-step2-high"
                        checked={form.enableStep2High}
                        onCheckedChange={(checked) =>
                          onUpdate({ enableStep2High: checked })
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="enable-step2-low" className="text-sm flex items-center gap-1.5">
                        <ThumbsDown className="h-3.5 w-3.5 text-orange-500" />
                        For low ratings (below {reviewSettings.starThreshold})
                      </Label>
                      <Switch
                        id="enable-step2-low"
                        checked={form.enableStep2Low}
                        onCheckedChange={(checked) =>
                          onUpdate({ enableStep2Low: checked })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Email & Rewards - High Rating */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-1.5">
                  <ThumbsUp className="h-4 w-4 text-green-500" />
                  High Rating Path ({reviewSettings.starThreshold}+ stars)
                </h4>

                <div className="pl-4 space-y-3 border-l-2 border-green-200 dark:border-green-900">
                  {/* Email Collection */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor="high-email" className="text-sm flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5" />
                      Collect email
                    </Label>
                    <Switch
                      id="high-email"
                      checked={reviewSettings.highRatingEmailEnabled}
                      onCheckedChange={(checked) =>
                        onUpdateReviewSettings({
                          highRatingEmailEnabled: checked,
                          highRatingEmailRequired: checked ? reviewSettings.highRatingEmailRequired : false,
                        })
                      }
                    />
                  </div>

                  {reviewSettings.highRatingEmailEnabled && (
                    <div className="flex items-center justify-between pl-4">
                      <Label htmlFor="high-email-required" className="text-xs text-muted-foreground">
                        Required
                      </Label>
                      <Switch
                        id="high-email-required"
                        checked={reviewSettings.highRatingEmailRequired}
                        onCheckedChange={(checked) =>
                          onUpdateReviewSettings({ highRatingEmailRequired: checked })
                        }
                      />
                    </div>
                  )}

                  {/* Reward Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1.5">
                      <Gift className="h-3.5 w-3.5" />
                      Reward
                    </Label>
                    <RadioGroup
                      value={reviewSettings.highRatingRewardType}
                      onValueChange={(value: RewardType) =>
                        onUpdateReviewSettings({
                          highRatingRewardType: value,
                          highRatingCouponOfferId: value === 'coupon' ? reviewSettings.highRatingCouponOfferId : null,
                          highRatingLoyaltyProgramId: value === 'loyalty' ? reviewSettings.highRatingLoyaltyProgramId : null,
                        })
                      }
                      className="flex flex-col gap-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="none" id="high-reward-none" />
                        <Label htmlFor="high-reward-none" className="text-sm font-normal cursor-pointer">
                          No reward
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="coupon" id="high-reward-coupon" />
                        <Label htmlFor="high-reward-coupon" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                          <Ticket className="h-3.5 w-3.5 text-amber-500" />
                          One-time coupon
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="loyalty" id="high-reward-loyalty" />
                        <Label htmlFor="high-reward-loyalty" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                          <Heart className="h-3.5 w-3.5 text-pink-500" />
                          Join loyalty program
                        </Label>
                      </div>
                    </RadioGroup>

                    {/* Coupon Offer Selector */}
                    {reviewSettings.highRatingRewardType === 'coupon' && (
                      <div className="pl-6 pt-1">
                        <Select
                          value={reviewSettings.highRatingCouponOfferId || ''}
                          onValueChange={(value) =>
                            onUpdateReviewSettings({ highRatingCouponOfferId: value || null })
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select coupon offer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {couponsLoading ? (
                              <SelectItem value="" disabled>Loading...</SelectItem>
                            ) : couponOffers.length === 0 ? (
                              <SelectItem value="" disabled>No active coupon offers</SelectItem>
                            ) : (
                              couponOffers.map((offer) => (
                                <SelectItem key={offer.id} value={offer.id}>
                                  {offer.name} ({offer.discount_type === 'percentage' ? `${offer.discount_value}%` : `${offer.discount_value}`})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Loyalty Program Selector */}
                    {reviewSettings.highRatingRewardType === 'loyalty' && (
                      <div className="pl-6 pt-1">
                        <Select
                          value={reviewSettings.highRatingLoyaltyProgramId || ''}
                          onValueChange={(value) =>
                            onUpdateReviewSettings({ highRatingLoyaltyProgramId: value || null })
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select loyalty program..." />
                          </SelectTrigger>
                          <SelectContent>
                            {loyaltyLoading ? (
                              <SelectItem value="" disabled>Loading...</SelectItem>
                            ) : loyaltyPrograms.length === 0 ? (
                              <SelectItem value="" disabled>No active loyalty programs</SelectItem>
                            ) : (
                              loyaltyPrograms.map((program) => (
                                <SelectItem key={program.id} value={program.id}>
                                  {program.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Email & Rewards - Low Rating */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-1.5">
                  <ThumbsDown className="h-4 w-4 text-orange-500" />
                  Low Rating Path (below {reviewSettings.starThreshold} stars)
                </h4>

                <div className="pl-4 space-y-3 border-l-2 border-orange-200 dark:border-orange-900">
                  {/* Email Collection */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor="low-email" className="text-sm flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5" />
                      Collect email
                    </Label>
                    <Switch
                      id="low-email"
                      checked={reviewSettings.lowRatingEmailEnabled}
                      onCheckedChange={(checked) =>
                        onUpdateReviewSettings({
                          lowRatingEmailEnabled: checked,
                          lowRatingEmailRequired: checked ? reviewSettings.lowRatingEmailRequired : false,
                        })
                      }
                    />
                  </div>

                  {reviewSettings.lowRatingEmailEnabled && (
                    <div className="flex items-center justify-between pl-4">
                      <Label htmlFor="low-email-required" className="text-xs text-muted-foreground">
                        Required
                      </Label>
                      <Switch
                        id="low-email-required"
                        checked={reviewSettings.lowRatingEmailRequired}
                        onCheckedChange={(checked) =>
                          onUpdateReviewSettings({ lowRatingEmailRequired: checked })
                        }
                      />
                    </div>
                  )}

                  {/* Reward Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1.5">
                      <Gift className="h-3.5 w-3.5" />
                      Reward
                    </Label>
                    <RadioGroup
                      value={reviewSettings.lowRatingRewardType}
                      onValueChange={(value: RewardType) =>
                        onUpdateReviewSettings({
                          lowRatingRewardType: value,
                          lowRatingCouponOfferId: value === 'coupon' ? reviewSettings.lowRatingCouponOfferId : null,
                          lowRatingLoyaltyProgramId: value === 'loyalty' ? reviewSettings.lowRatingLoyaltyProgramId : null,
                        })
                      }
                      className="flex flex-col gap-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="none" id="low-reward-none" />
                        <Label htmlFor="low-reward-none" className="text-sm font-normal cursor-pointer">
                          No reward
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="coupon" id="low-reward-coupon" />
                        <Label htmlFor="low-reward-coupon" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                          <Ticket className="h-3.5 w-3.5 text-amber-500" />
                          One-time coupon
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="loyalty" id="low-reward-loyalty" />
                        <Label htmlFor="low-reward-loyalty" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                          <Heart className="h-3.5 w-3.5 text-pink-500" />
                          Join loyalty program
                        </Label>
                      </div>
                    </RadioGroup>

                    {/* Coupon Offer Selector */}
                    {reviewSettings.lowRatingRewardType === 'coupon' && (
                      <div className="pl-6 pt-1">
                        <Select
                          value={reviewSettings.lowRatingCouponOfferId || ''}
                          onValueChange={(value) =>
                            onUpdateReviewSettings({ lowRatingCouponOfferId: value || null })
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select coupon offer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {couponsLoading ? (
                              <SelectItem value="" disabled>Loading...</SelectItem>
                            ) : couponOffers.length === 0 ? (
                              <SelectItem value="" disabled>No active coupon offers</SelectItem>
                            ) : (
                              couponOffers.map((offer) => (
                                <SelectItem key={offer.id} value={offer.id}>
                                  {offer.name} ({offer.discount_type === 'percentage' ? `${offer.discount_value}%` : `${offer.discount_value}`})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Loyalty Program Selector */}
                    {reviewSettings.lowRatingRewardType === 'loyalty' && (
                      <div className="pl-6 pt-1">
                        <Select
                          value={reviewSettings.lowRatingLoyaltyProgramId || ''}
                          onValueChange={(value) =>
                            onUpdateReviewSettings({ lowRatingLoyaltyProgramId: value || null })
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select loyalty program..." />
                          </SelectTrigger>
                          <SelectContent>
                            {loyaltyLoading ? (
                              <SelectItem value="" disabled>Loading...</SelectItem>
                            ) : loyaltyPrograms.length === 0 ? (
                              <SelectItem value="" disabled>No active loyalty programs</SelectItem>
                            ) : (
                              loyaltyPrograms.map((program) => (
                                <SelectItem key={program.id} value={program.id}>
                                  {program.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Display Settings */}
          <TabsContent value="display" className="space-y-4 pt-4">
            {/* Animations */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="animations-enabled">Animations</Label>
                <p className="text-xs text-muted-foreground">
                  Smooth transitions between steps
                </p>
              </div>
              <Switch
                id="animations-enabled"
                checked={form.animationSettings.enabled}
                onCheckedChange={(checked) =>
                  onUpdate({
                    animationSettings: { ...form.animationSettings, enabled: checked },
                  })
                }
              />
            </div>

            <Separator />

            {/* Progress Bar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="progress-enabled">Progress bar</Label>
                  <p className="text-xs text-muted-foreground">
                    Show completion progress
                  </p>
                </div>
                <Switch
                  id="progress-enabled"
                  checked={form.progressBarConfig.enabled}
                  onCheckedChange={(checked) =>
                    onUpdate({
                      progressBarConfig: { ...form.progressBarConfig, enabled: checked },
                    })
                  }
                />
              </div>

              {form.progressBarConfig.enabled && (
                <div className="flex items-center justify-between pl-4">
                  <Label htmlFor="progress-percentage" className="text-sm text-muted-foreground">
                    Show percentage
                  </Label>
                  <Switch
                    id="progress-percentage"
                    checked={form.progressBarConfig.showPercentage}
                    onCheckedChange={(checked) =>
                      onUpdate({
                        progressBarConfig: {
                          ...form.progressBarConfig,
                          showPercentage: checked,
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Step Indicator */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="step-indicator-enabled">Step indicator</Label>
                  <p className="text-xs text-muted-foreground">
                    Show "Step X / Y" with arrows
                  </p>
                </div>
                <Switch
                  id="step-indicator-enabled"
                  checked={form.stepIndicatorConfig?.enabled ?? false}
                  onCheckedChange={(checked) =>
                    onUpdate({
                      stepIndicatorConfig: {
                        ...form.stepIndicatorConfig,
                        enabled: checked,
                        showArrows: form.stepIndicatorConfig?.showArrows ?? true,
                      },
                    })
                  }
                />
              </div>

              {form.stepIndicatorConfig?.enabled && (
                <div className="flex items-center justify-between pl-4">
                  <Label htmlFor="step-arrows" className="text-sm text-muted-foreground">
                    Show navigation arrows
                  </Label>
                  <Switch
                    id="step-arrows"
                    checked={form.stepIndicatorConfig.showArrows}
                    onCheckedChange={(checked) =>
                      onUpdate({
                        stepIndicatorConfig: {
                          ...form.stepIndicatorConfig,
                          showArrows: checked,
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Text Customization */}
          <TabsContent value="text" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rating-title" className="text-sm">Rating Title</Label>
                <Input
                  id="rating-title"
                  value={form.uiTextConfig?.ratingTitle || ''}
                  onChange={(e) => onUpdateUIText('ratingTitle', e.target.value)}
                  placeholder="How was your experience?"
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rating-subtitle" className="text-sm">Rating Subtitle</Label>
                <Input
                  id="rating-subtitle"
                  value={form.uiTextConfig?.ratingSubtitle || ''}
                  onChange={(e) => onUpdateUIText('ratingSubtitle', e.target.value)}
                  placeholder="Tap a star to rate"
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="thankyou-title" className="text-sm">Thank You Title</Label>
                <Input
                  id="thankyou-title"
                  value={form.uiTextConfig?.thankYouTitle || ''}
                  onChange={(e) => onUpdateUIText('thankYouTitle', e.target.value)}
                  placeholder="Thank you!"
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="thankyou-message" className="text-sm">Thank You Message</Label>
                <Input
                  id="thankyou-message"
                  value={form.uiTextConfig?.thankYouMessage || ''}
                  onChange={(e) => onUpdateUIText('thankYouMessage', e.target.value)}
                  placeholder="Your feedback helps us improve."
                  className="h-8 text-sm"
                />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="next-button" className="text-sm">Next Button</Label>
                <Input
                  id="next-button"
                  value={form.uiTextConfig?.nextButtonText || ''}
                  onChange={(e) => onUpdateUIText('nextButtonText', e.target.value)}
                  placeholder="Next"
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="back-button" className="text-sm">Back Button</Label>
                <Input
                  id="back-button"
                  value={form.uiTextConfig?.backButtonText || ''}
                  onChange={(e) => onUpdateUIText('backButtonText', e.target.value)}
                  placeholder="Back"
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="submit-button" className="text-sm">Submit Button</Label>
                <Input
                  id="submit-button"
                  value={form.uiTextConfig?.submitButtonText || ''}
                  onChange={(e) => onUpdateUIText('submitButtonText', e.target.value)}
                  placeholder="Submit"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
